import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";

import { skipToken, useQuery } from "@tanstack/react-query";
import { ActiveElement, Chart, ChartEvent } from "chart.js";
import "chartjs-adapter-moment";
import ChartAnnotation, { AnnotationPluginOptions } from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment";
import { validate as uuidValidate } from "uuid";

import { getFieldShapeAdmin, getNdviScores } from "services";

import { useGetFieldPractices, useLocation } from "utils";

import { DEFAULT_CACHE_TIME, GET_FIELD_SHAPE_ADMIN, GET_NDVI_SCORES, LANGUAGE_CODE } from "consts";

import { AdminHead, Button, Col, Form, Input, MainPage, Option, Row, Select, SkeletonWrapper, TagNormal, Message as message } from "components";

import { CropMap } from "pages/admin/verifications/CropMap";
import { CropsOverviewTable } from "pages/admin/verifications/CropsOverviewTable";

const cacheTime = {
  gcTime: DEFAULT_CACHE_TIME,
  staleTime: DEFAULT_CACHE_TIME,
};

Chart.register(ChartAnnotation, ChartDataLabels);

const defaultZoom = 15;
const possibleYearsCount = 6;
const currentYear = moment().year();
const possibleYears = Array.from({ length: possibleYearsCount }, (_, i) => currentYear - possibleYearsCount + i + 1);
const defaultStartYear = possibleYears[1];
const defaultEndYear = possibleYears[possibleYearsCount - 2];
const possibleCloudCover = [5, 10, 15, 20, 25, 30];
const defaultCloudCover = possibleCloudCover[possibleCloudCover.length - 1];

export const NdviScores = () => {
  const [fieldId, setFieldId] = useState<string | null>();
  const [selectedDates, setSelectedDates] = useState<[string | null, string | null]>([null, null]);
  const location = useLocation();
  const [yearForm] = Form.useForm();
  const startYear = Form.useWatch("startYear", yearForm);
  const endYear = Form.useWatch("endYear", yearForm);
  const cloudCover = Form.useWatch("cloudCover", yearForm);

  // set the language
  moment.locale(LANGUAGE_CODE.ENGLISH);

  const { data: fieldShapeData, isLoading: isLoadingFieldShape } = useQuery({
    queryKey: [GET_FIELD_SHAPE_ADMIN, fieldId],
    queryFn: fieldId ? () => getFieldShapeAdmin({ fieldId }) : skipToken,
    ...cacheTime,
  });

  const { data: ndviScores, isLoading: isLoadingNdviScores } = useQuery({
    queryKey: [GET_NDVI_SCORES, fieldShapeData?.fieldShape, startYear, endYear, cloudCover],
    queryFn:
      fieldShapeData?.fieldShape && startYear <= endYear
        ? () => getNdviScores({ ...fieldShapeData.fieldShape, timeSpan: { startYear, endYear }, cloudCover })
        : skipToken,
    ...cacheTime,
  });

  const { data: fieldPractices, isLoading: isLoadingFieldPractices } = useGetFieldPractices({
    fieldId: fieldId ?? "",
    inView: !!fieldId && uuidValidate(fieldId),
    isAdmin: true,
  });

  const searchField = (v: { fieldId: string }) => {
    if (v.fieldId) {
      setFieldId(v.fieldId);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.has("fieldId")) {
      setFieldId(params.get("fieldId"));
    }
  }, [location.search]);

  useEffect(() => {
    if (startYear > endYear) {
      message({ content: "Start year cannot be higher than end year", icon: "gremlin" });
    }
  }, [startYear, endYear]);

  const dates = ndviScores?.map(({ date }) => date) ?? [];

  const cropBoxes: AnnotationPluginOptions["annotations"] = {};

  const center = fieldShapeData?.fieldShape.center;

  const tag = <TagNormal text="ALPHA" style={{ transform: "translate(0, -50%)", fontWeight: "bold" }} />;

  const handleChartClick = (event: ChartEvent, elements: ActiveElement[]) => {
    if (elements.length === 0) {
      return;
    }

    // We assume there was just one element clicked on
    const foundDate = dates[elements[0].index];

    if (!foundDate) {
      return;
    }

    // If user presses down shift key we set the second date;
    // @ts-expect-error shiftKey is there, seems like it is not correctly typed
    const shouldSetSecond = event.native?.shiftKey;

    setSelectedDates((selectedDates) => (shouldSetSecond ? [selectedDates[0], foundDate] : [foundDate, selectedDates[1]]));
  };

  return (
    <MainPage pageName={"admin"}>
      <AdminHead
        headline={<>NDVI scores {tag}</>}
        description={
          <>
            Pass a field id via the query params to fetch and display NDVI scores for this field. <br />
            You can click on the points in the graph to select the date as a flyover for the map. Hold down SHIFT key to assign it to the map on the right.
          </>
        }
      />

      <Row>
        <Col>{!fieldId ? <p>You need to pass in a "fieldId" via query params or search a field.</p> : <p>Field id: {fieldId}</p>}</Col>
      </Row>

      <Form onFinish={searchField}>
        <Row align="middle">
          <Col md={8}>
            <Input name="fieldId" label="Field id" rules={[{ required: true }]} />
          </Col>

          <Col md={4}>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isLoadingFieldShape}>
                Search field
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Form className="margin-bottom-200 margin-top-100" form={yearForm}>
        <Row>
          <Col md={6}>
            <YearSelect name="startYear" label="Select a start year" initialValue={defaultStartYear} />
          </Col>
          <Col md={6}>
            <YearSelect name="endYear" label="Select an end year" initialValue={defaultEndYear} />
          </Col>
          <Col md={4}>
            <CloudCoverageSelect name="cloudCover" label="Select a cloud coverage" initialValue={defaultCloudCover} />
          </Col>
        </Row>
      </Form>

      <SkeletonWrapper isLoading={isLoadingNdviScores} type="block" height="30rem">
        <Row>
          <Col>
            <Line
              data={{
                datasets: [
                  {
                    label: "NDVI",
                    data: ndviScores?.map(({ date, ndvi }) => ({ x: date, y: parseFloat(ndvi.mean.toFixed(3)) })),
                    borderColor: "#4d85f5",
                    tension: 0.4,
                  },
                  {
                    label: "NDVI min",
                    hidden: true,
                    data: ndviScores?.map(({ date, ndvi }) => ({ x: date, y: parseFloat(ndvi.min.toFixed(3)) })),
                    borderColor: "#db3804",
                    tension: 0.4,
                  },
                  {
                    label: "NDVI max",
                    hidden: true,
                    data: ndviScores?.map(({ date, ndvi }) => ({ x: date, y: parseFloat(ndvi.max.toFixed(3)) })),
                    borderColor: "#065c45",
                    tension: 0.4,
                  },
                ],
              }}
              options={{
                onClick: handleChartClick,
                scales: {
                  x: {
                    type: "time",
                    time: {
                      unit: "month",
                    },
                    min: moment({ year: startYear, month: 0, day: 1 }).toISOString(),
                    max: moment({ year: endYear + 1, month: 0, day: 1 }).toISOString(),
                  },
                },
                plugins: {
                  datalabels: {
                    display: false,
                  },
                  annotation: {
                    annotations: cropBoxes,
                  },
                  tooltip: {
                    callbacks: {
                      label: ({ dataIndex, datasetIndex }) => {
                        // Get ndvi object from array to also render min and max
                        const ndvi = ndviScores?.[dataIndex]?.ndvi;

                        if (!ndvi) return "";

                        switch (datasetIndex) {
                          case 0:
                            return `Mean: ${parseFloat(ndvi.mean.toFixed(3))}`;
                          case 1:
                            return `Min: ${parseFloat(ndvi.min.toFixed(3))}`;
                          case 2:
                            return `Max: ${parseFloat(ndvi.max.toFixed(3))}`;
                          default:
                            return "";
                        }
                      },
                    },
                  },
                },
              }}
            />
          </Col>
        </Row>
      </SkeletonWrapper>

      <CropsOverviewTable isLoadingCropsData={isLoadingFieldPractices} cropsData={fieldPractices?.crops} startYear={startYear} endYear={endYear} />

      <AdminHead headline={<>Crop visualisations {tag}</>} description="" className="margin-top-200" />

      <SkeletonWrapper isLoading={isLoadingFieldShape} type="block" className="margin-top-200">
        <Row className="margin-top-200">
          <Col sm={24} md={12}>
            <CropMap
              geometry={fieldShapeData?.fieldShape}
              location={center}
              defaultZoom={defaultZoom}
              selectedDate={selectedDates[0]}
              timeSpan={{ startYear, endYear }}
            />
          </Col>
          <Col sm={24} md={12}>
            <CropMap
              geometry={fieldShapeData?.fieldShape}
              location={center}
              defaultZoom={defaultZoom}
              selectedDate={selectedDates[1]}
              timeSpan={{ startYear, endYear }}
            />
          </Col>
        </Row>
        <Row>
          <Col>Made with ♡ by Adiv & Vali</Col>
        </Row>
      </SkeletonWrapper>
    </MainPage>
  );
};

const YearSelect = ({ name, label, initialValue }: { name: string; label: string; initialValue: number }) => (
  // @ts-expect-error not yet ts
  <Select name={name} label={label} rules={[{ required: true }]} optionFilterProp="children" optionLabelProp={"label"} initialValue={initialValue}>
    {possibleYears.map((year) => (
      <Option value={year} key={year} label={year}>
        {year}
      </Option>
    ))}
  </Select>
);

const CloudCoverageSelect = ({ name, label, initialValue }: { name: string; label: string; initialValue: number }) => (
  // @ts-expect-error not yet ts
  <Select name={name} label={label} rules={[{ required: true }]} optionFilterProp="children" optionLabelProp={"label"} initialValue={initialValue}>
    {possibleCloudCover.map((cloudCover) => (
      <Option value={cloudCover} key={cloudCover} label={`${cloudCover}%`}>
        {cloudCover}%
      </Option>
    ))}
  </Select>
);
